/**
 * =============================================================================
 * COD Checkout — JavaScript Controller
 * =============================================================================
 * Vanilla JS (no jQuery). Handles:
 *  1. Fetching cart data from /cart.js
 *  2. Rendering order summary with live quantity updates
 *  3. Form validation with inline errors
 *  4. Order submission via Shopify checkout redirect (pre-filled fields)
 *  5. Bot protection (honeypot)
 *  6. Double-submit prevention
 * =============================================================================
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  CONFIG — pulled from data attributes on #cod-checkout-root         */
  /* ------------------------------------------------------------------ */
  const root = document.getElementById('cod-checkout-root');
  if (!root) return;

  const CONFIG = {
    moneyFormat       : root.dataset.moneyFormat      || '{{amount}}',
    currency          : root.dataset.currency          || '',
    shippingFee       : parseFloat(root.dataset.shippingFee || '0'),
    shippingLabel     : root.dataset.shippingLabel     || 'Shipping',
    phonePrefix       : root.dataset.phonePrefix       || '+7',
    thankYouUrl       : root.dataset.thankYouUrl       || '/pages/thank-you',
    shopDomain        : root.dataset.shopDomain        || window.location.hostname,
    apiEndpoint       : root.dataset.apiEndpoint       || '',
    webhookSecret     : root.dataset.webhookSecret     || '',
  };

  /* ------------------------------------------------------------------ */
  /*  DOM REFS                                                           */
  /* ------------------------------------------------------------------ */
  const summaryList       = root.querySelector('#cod-summary-items');
  const summaryLoading    = root.querySelector('#cod-summary-loading');
  const summaryEmpty      = root.querySelector('#cod-summary-empty');
  const summaryContent    = root.querySelector('#cod-summary-content');
  const subtotalEl        = root.querySelector('#cod-subtotal');
  const shippingEl        = root.querySelector('#cod-shipping');
  const totalEl           = root.querySelector('#cod-total');
  const form              = root.querySelector('#cod-form');
  const submitBtn         = root.querySelector('#cod-submit-btn');
  const formError         = root.querySelector('#cod-form-error');

  let cartData = null;
  let isSubmitting = false;

  /* ------------------------------------------------------------------ */
  /*  MONEY FORMATTER                                                    */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents) {
    const amountRaw = cents / 100;
    const amountDec = amountRaw.toFixed(2);
    const amountInt = Math.round(amountRaw);

    // Formatted variants Shopify might need
    const amount            = parseFloat(amountDec).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const amountNoDec       = amountInt.toLocaleString('en-US');
    const amountComma       = amount.replace('.', ',');
    const amountNoDecComma  = amountInt.toLocaleString('ru-RU'); // space as thousand sep

    let fmt = CONFIG.moneyFormat;

    // Replace all Shopify format tokens (handles spaces inside braces, HTML entities)
    fmt = fmt.replace(/\{\{[\s]*amount_with_comma_separator[\s]*\}\}/gi, amountComma);
    fmt = fmt.replace(/\{\{[\s]*amount_no_decimals_with_comma_separator[\s]*\}\}/gi, amountNoDecComma);
    fmt = fmt.replace(/\{\{[\s]*amount_no_decimals[\s]*\}\}/gi, amountNoDec);
    fmt = fmt.replace(/\{\{[\s]*amount[\s]*\}\}/gi, amount);

    // Fallback: if any {{ }} tokens still remain, the format didn't match.
    // Just show currency + number directly.
    if (fmt.indexOf('{{') !== -1 || fmt.indexOf('amount') !== -1) {
      // Extract currency symbol (everything before the first { or letter 'a')
      var symbol = CONFIG.moneyFormat.replace(/\{.*/, '').trim() || '₸';
      return symbol + ' ' + amountInt.toLocaleString('ru-RU');
    }

    return fmt;
  }

  /* ------------------------------------------------------------------ */
  /*  FETCH CART                                                         */
  /* ------------------------------------------------------------------ */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Не удалось загрузить корзину');
      cartData = await res.json();
      renderSummary();

      // ── Meta Pixel: InitiateCheckout on page load ──
      if (typeof fbq === 'function' && cartData && cartData.item_count > 0) {
        try {
          fbq('track', 'InitiateCheckout', {
            content_ids: cartData.items.map(function (item) { return String(item.variant_id); }),
            contents: cartData.items.map(function (item) {
              return { id: String(item.variant_id), quantity: item.quantity };
            }),
            content_type: 'product',
            num_items: cartData.item_count,
            value: (cartData.total_price / 100),
            currency: CONFIG.currency || 'KZT',
          });
        } catch (fbErr) {
          console.warn('[COD Checkout] Meta Pixel InitiateCheckout error:', fbErr);
        }
      }

    } catch (err) {
      console.error('[COD Checkout] Cart fetch error:', err);
      showEmpty();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  RENDER ORDER SUMMARY                                               */
  /* ------------------------------------------------------------------ */
  function renderSummary() {
    if (summaryLoading) summaryLoading.style.display = 'none';

    if (!cartData || cartData.item_count === 0) {
      showEmpty();
      return;
    }

    if (summaryEmpty) summaryEmpty.style.display = 'none';
    if (summaryContent) summaryContent.style.display = 'block';

    // Build line items
    let html = '';
    cartData.items.forEach(function (item) {
      const imgSrc = item.featured_image
        ? item.featured_image.url.replace(/(\.[a-z]+)(\?|$)/, '_180x180$1$2')
        : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
      const variantTitle = item.variant_title || '';
      html += `
        <li class="cod-checkout__item" data-line-key="${item.key}">
          <img class="cod-checkout__item-image"
               src="${imgSrc}"
               alt="${escapeHtml(item.product_title)}"
               loading="lazy" width="64" height="64">
          <div class="cod-checkout__item-details">
            <div class="cod-checkout__item-title">${escapeHtml(item.product_title)}</div>
            ${variantTitle ? `<div class="cod-checkout__item-variant">${escapeHtml(variantTitle)}</div>` : ''}
            <div class="cod-checkout__item-qty">
              <button type="button" class="cod-checkout__qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">&minus;</button>
              <span class="cod-checkout__qty-val">${item.quantity}</span>
              <button type="button" class="cod-checkout__qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <div class="cod-checkout__item-right">
            <button type="button" class="cod-checkout__item-remove" data-key="${item.key}" aria-label="Remove ${escapeHtml(item.product_title)}" title="Remove">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div class="cod-checkout__item-price">${formatMoney(item.final_line_price)}</div>
          </div>
        </li>`;
    });
    summaryList.innerHTML = html;

    // Totals
    updateTotals();

    // Bind qty buttons
    summaryList.querySelectorAll('.cod-checkout__qty-btn').forEach(function (btn) {
      btn.addEventListener('click', handleQtyChange);
    });

    // Bind remove buttons
    summaryList.querySelectorAll('.cod-checkout__item-remove').forEach(function (btn) {
      btn.addEventListener('click', handleRemoveItem);
    });

    // Enable form
    if (form) form.style.display = '';
  }

  function updateTotals() {
    if (!cartData) return;
    const subtotal = cartData.total_price;
    const shippingCents = Math.round(CONFIG.shippingFee * 100);
    const total = subtotal + shippingCents;

    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
    if (shippingEl) shippingEl.textContent = shippingCents === 0 ? 'Бесплатно' : formatMoney(shippingCents);
    if (totalEl) totalEl.textContent = formatMoney(total);
  }

  function showEmpty() {
    if (summaryLoading) summaryLoading.style.display = 'none';
    if (summaryContent) summaryContent.style.display = 'none';
    if (summaryEmpty) summaryEmpty.style.display = 'block';
    if (form) form.style.display = 'none';
  }

  /* ------------------------------------------------------------------ */
  /*  QUANTITY CHANGE                                                    */
  /* ------------------------------------------------------------------ */
  async function handleQtyChange(e) {
    const btn = e.currentTarget;
    const key = btn.dataset.key;
    const action = btn.dataset.action;
    const item = cartData.items.find(function (i) { return i.key === key; });
    if (!item) return;

    const newQty = action === 'increase' ? item.quantity + 1 : item.quantity - 1;
    if (newQty < 0) return;

    btn.disabled = true;

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty }),
      });
      if (!res.ok) throw new Error('Не удалось обновить количество');
      cartData = await res.json();
      renderSummary();
    } catch (err) {
      console.error('[COD Checkout] Qty change error:', err);
      btn.disabled = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  REMOVE ITEM                                                        */
  /* ------------------------------------------------------------------ */
  async function handleRemoveItem(e) {
    const btn = e.currentTarget;
    const key = btn.dataset.key;
    const itemEl = btn.closest('.cod-checkout__item');

    // Animate removal
    if (itemEl) {
      itemEl.classList.add('cod-checkout__item--removing');
    }

    btn.disabled = true;

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!res.ok) throw new Error('Не удалось удалить товар');
      cartData = await res.json();

      // Wait for animation then re-render
      setTimeout(function () {
        renderSummary();
      }, 300);
    } catch (err) {
      console.error('[COD Checkout] Remove error:', err);
      if (itemEl) itemEl.classList.remove('cod-checkout__item--removing');
      btn.disabled = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  FORM VALIDATION                                                    */
  /* ------------------------------------------------------------------ */
  function validateForm() {
    let isValid = true;

    // Clear previous errors
    form.querySelectorAll('.cod-field--error').forEach(function (f) {
      f.classList.remove('cod-field--error');
    });

    // Check honeypot
    const hp = form.querySelector('[name="cod_hp_field"]');
    if (hp && hp.value.trim() !== '') {
      // Silently reject bots
      return false;
    }

    // Validate required fields
    const requiredInputs = form.querySelectorAll('[data-required="true"]');
    requiredInputs.forEach(function (input) {
      const field = input.closest('.cod-field');
      if (!field) return;

      const val = input.value.trim();
      if (!val) {
        field.classList.add('cod-field--error');
        const errEl = field.querySelector('.cod-field__error');
        if (errEl) errEl.textContent = 'Заполните это поле';
        isValid = false;
        return;
      }

      // Phone validation (digits only, reasonable length)
      if (input.dataset.fieldType === 'phone') {
        const digitsOnly = val.replace(/[^0-9]/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) {
          field.classList.add('cod-field--error');
          const errEl = field.querySelector('.cod-field__error');
          if (errEl) errEl.textContent = 'Введите корректный номер телефона';
          isValid = false;
        }
      }

      // Email validation (if present and required)
      if (input.type === 'email' && val) {
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(val)) {
          field.classList.add('cod-field--error');
          const errEl = field.querySelector('.cod-field__error');
          if (errEl) errEl.textContent = 'Введите корректный email';
          isValid = false;
        }
      }
    });

    // Also validate optional email format if filled
    const emailInput = form.querySelector('[data-field-type="email"]');
    if (emailInput && emailInput.value.trim() && emailInput.dataset.required !== 'true') {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(emailInput.value.trim())) {
        const field = emailInput.closest('.cod-field');
        if (field) {
          field.classList.add('cod-field--error');
          const errEl = field.querySelector('.cod-field__error');
          if (errEl) errEl.textContent = 'Введите корректный email';
          isValid = false;
        }
      }
    }

    return isValid;
  }

  // Real-time: clear error on input
  if (form) {
    form.addEventListener('input', function (e) {
      const field = e.target.closest('.cod-field');
      if (field && field.classList.contains('cod-field--error')) {
        field.classList.remove('cod-field--error');
      }
      if (formError) formError.classList.remove('cod-checkout__form-error--visible');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  ORDER SUBMISSION                                                   */
  /* ------------------------------------------------------------------ */
  /**
   * Strategy: POST customer info + cart line items to our backend server,
   * which creates the order via Shopify Admin API. No redirect to native
   * checkout — fully custom, seamless flow.
   *
   * Requires: a deployed backend server (see cod-server/).
   */
  async function submitOrder(e) {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) {
      const firstErr = form.querySelector('.cod-field--error');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Verify API endpoint is configured
    if (!CONFIG.apiEndpoint) {
      console.error('[COD Checkout] API endpoint not configured. Set data-api-endpoint on #cod-checkout-root.');
      if (formError) {
        formError.textContent = 'Оформление временно недоступно. Пожалуйста, свяжитесь с нами.';
        formError.classList.add('cod-checkout__form-error--visible');
      }
      return;
    }

    isSubmitting = true;
    submitBtn.disabled = true;
    submitBtn.classList.add('cod-checkout__submit--loading');
    if (formError) formError.classList.remove('cod-checkout__form-error--visible');

    // Collect form data — use multiple strategies to find field values
    // (iOS Safari sometimes doesn't return values from readonly inputs or selects)
    const getValue = function (name) {
      // Try by name attribute inside form
      var el = form.querySelector('[name="' + name + '"]');
      if (el && el.value && el.value.trim()) return el.value.trim();

      // Try by id (cod-fieldname)
      el = document.getElementById('cod-' + name);
      if (el && el.value && el.value.trim()) return el.value.trim();

      // Try by name attribute anywhere on the page (in case field is outside form)
      el = document.querySelector('[name="' + name + '"]');
      if (el && el.value && el.value.trim()) return el.value.trim();

      return '';
    };

    const fullName   = getValue('full_name');
    const phone      = CONFIG.phonePrefix + getValue('phone');
    const email      = getValue('email');
    const country    = getValue('country');
    // Address is no longer collected on the form — the manager confirms it by phone.
    // Send a placeholder so the backend (which requires city/address) accepts the order.
    const addressFallback = 'Вам напишут в WhatsApp для уточнения';
    const city       = getValue('city') || addressFallback;
    const district   = getValue('district');
    const address    = getValue('address') || addressFallback;
    const zip        = getValue('zip');
    const notes      = getValue('order_notes');
    const paymentMethod = getValue('payment_method');
    const honeypot   = getValue('cod_hp_field');

    // ── Collect Meta CAPI data ──
    function getCookie(name) {
      var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    }

    // Landing/ad-click URL captured on entry (see snippets/landing-url-capture.liquid).
    // Falls back to the current URL if nothing was stored.
    function getLandingUrl() {
      try {
        return window.localStorage.getItem('avelia_landing_url') || window.location.href;
      } catch (e) {
        return window.location.href;
      }
    }

    const metaData = {
      fbc: getCookie('_fbc'),           // Meta Click ID
      fbp: getCookie('_fbp'),           // Meta Browser ID
      user_agent: navigator.userAgent,  // Client User Agent
      url_source: getLandingUrl(),      // Landing URL w/ UTMs for Meta event_source_url
    };

    try {
      // Build request payload
      const payload = {
        customer: {
          full_name: fullName,
          phone: phone,
          email: email || '',
          country: country,
          city: city,
          district: district,
          address: address,
          zip: zip,
        },
        line_items: cartData.items.map(function (item) {
          return {
            variant_id: item.variant_id,
            quantity: item.quantity,
          };
        }),
        note: notes,
        payment_method: paymentMethod,
        shipping_fee: CONFIG.shippingFee,
        honeypot: honeypot,
        meta: metaData,
      };

      // Build request headers
      const headers = { 'Content-Type': 'application/json' };
      if (CONFIG.webhookSecret) {
        headers['X-Webhook-Secret'] = CONFIG.webhookSecret;
      }

      // POST to backend API
      const res = await fetch(CONFIG.apiEndpoint + '/api/create-order', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Не удалось оформить заказ');
      }

      // Order created successfully — store confirmation for thank-you page
      const orderSummary = {
        order_name: data.order.name,
        order_number: data.order.order_number,
        name: fullName,
        phone: phone,
        email: email,
        address: [address, district, city, country, zip].filter(function (v, i, a) { return v && a.indexOf(v) === i; }).join(', '),
        items: data.order.line_items.map(function (li) {
          return {
            title: li.title,
            variant: li.variant_title,
            quantity: li.quantity,
            price: li.price,
          };
        }),
        total_price: data.order.total_price,
        subtotal_price: data.order.subtotal_price,
        currency: data.order.currency,
        shipping: CONFIG.shippingFee === 0 ? 'Бесплатно' : CONFIG.shippingFee,
        status_url: data.order.status_url,
        date: new Date().toLocaleDateString(),
      };

      try {
        sessionStorage.setItem('cod_order_summary', JSON.stringify(orderSummary));
      } catch (storageErr) {
        // Non-critical
      }

      // Clear the cart
      try {
        await fetch('/cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (clearErr) {
        console.warn('[COD Checkout] Cart clear failed:', clearErr);
      }

      // Redirect to thank-you page
      window.location.href = CONFIG.thankYouUrl;

    } catch (err) {
      console.error('[COD Checkout] Submission error:', err);
      isSubmitting = false;
      submitBtn.disabled = false;
      submitBtn.classList.remove('cod-checkout__submit--loading');
      if (formError) {
        formError.textContent = err.message || 'Что-то пошло не так. Попробуйте ещё раз.';
        formError.classList.add('cod-checkout__form-error--visible');
        formError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  if (form) {
    form.addEventListener('submit', submitOrder);
  }

  /* ------------------------------------------------------------------ */
  /*  HELPERS                                                            */
  /* ------------------------------------------------------------------ */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /* ------------------------------------------------------------------ */
  /*  INIT                                                               */
  /* ------------------------------------------------------------------ */
  console.log('[COD Checkout] Money format from theme:', JSON.stringify(CONFIG.moneyFormat));
  fetchCart();
})();